import { useTranslation } from 'react-i18next';

import { formatScope, lineageReasonText } from '../utils/lineage-images-format';
import { batchReferenceReason, ordinal, type StageRetryGroup } from '../utils/product-history';

import { RunNameLink } from './run-name';

/** One real retry's attempt list, oldest first: "1st failed — host timeout · 2nd ✓". */
function RetryAttempts({ attempts }: { attempts: StageRetryGroup['attempts'] }) {
  const { t } = useTranslation('catalog-v2-product-master');
  const { t: tLineage } = useTranslation('catalog-v2-lineage-images');
  return (
    <>
      {attempts.map((a, i) => {
        const ref = a.status === 'FAILED' ? batchReferenceReason(a) : null;
        const reason = a.status === 'FAILED' && !ref ? lineageReasonText(tLineage, a) : null;
        return (
          <span key={`${String(a.attempt)}|${a.createdAt}`}>
            {a.status !== 'FAILED' ? (
              t('productHistory.retryAttemptOk', { ordinal: ordinal(a.attempt) })
            ) : ref ? (
              <>
                {t('productHistory.retryAttemptFailedNoReason', { ordinal: ordinal(a.attempt) })} —{' '}
                {t(ref.kind === 'held' ? 'journey.heldByBatch' : 'journey.supersededByBatch')}{' '}
                <RunNameLink runId={ref.runId} className="text-xs" />
              </>
            ) : reason ? (
              t('productHistory.retryAttemptFailed', { ordinal: ordinal(a.attempt), reason })
            ) : (
              t('productHistory.retryAttemptFailedNoReason', { ordinal: ordinal(a.attempt) })
            )}
            {i < attempts.length - 1 ? ' · ' : ''}
          </span>
        );
      })}
    </>
  );
}

/**
 * One ↻ line: "↻ Matching retried once (UK · en): 1st failed — host timeout · 2nd ✓" — only
 * rendered for a stage that was genuinely retried (design round 2, item 4B/7 — never the old
 * "N attempts: 1st ok, 1st ok" line for a stage that merely ran more than once). Shared by the
 * product history card (one per batch row) and the journey drawer (one per reached ledger stage).
 */
export function StageRetryNote({
  group,
  stageLabel,
}: {
  group: StageRetryGroup;
  stageLabel: string;
}) {
  const { t } = useTranslation('catalog-v2-product-master');
  const market = group.scope ? formatScope(group.scope) : null;
  const count = group.attempts.length - 1;
  return (
    <p className="text-muted-foreground flex flex-wrap items-baseline gap-x-1 pl-4 text-xs">
      <span>
        {market
          ? t('productHistory.retryHeading', { count, stage: stageLabel, market })
          : t('productHistory.retryHeadingNoMarket', { count, stage: stageLabel })}
      </span>
      <RetryAttempts attempts={group.attempts} />
    </p>
  );
}
